import { Dictionary } from 'lodash';
import sumBy from 'lodash/sumBy';

import { ChartDatum, PieDatum } from '@/domain/models/Chart';
import { Days } from '@/domain/models/Day';
import { Hours } from '@/domain/models/Hour';
import { Item, Order } from '@/domain/models/Order';
import { ItemDomainService, OrderDomainService } from '@/domain/services';
import { DateDomainService } from '@/domain/services/Date/DateDomainService';

export class ChartDataDomainService {
  static projectToTotalPriceDatumByDate(orders: Order[]): ChartDatum | null {
    if (orders.length <= 0) return null;

    const groups = OrderDomainService.createDictionaryByDate(orders);
    const dateList = Object.keys(groups).map((date) => new Date(date));
    const xList = DateDomainService.rangeFromMinToMax(dateList);

    const totalPriceDatum: ChartDatum = {
      id: 'totalPrice',
      data: xList.map((date) => {
        const x = DateDomainService.format(date);
        const y = sumBy(groups[x], (o) => OrderDomainService.totalPrice(o));
        return { x, y };
      }),
    };
    return totalPriceDatum;
  }

  static projectToStoreRatio(
    orders: Order[],
    method:
      | 'createDictionaryByBrandName'
      | 'createDictionaryByAuthStore'
      | 'createDictionaryByStoreName'
      | 'createDictionaryByDeliveryServiceName'
      | 'createDictionaryByDeliveryType'
  ): ChartDatum | null {
    if (orders.length <= 0) return null;

    const groups = OrderDomainService[method](orders);

    const totalPriceDatum = this.totalPriceByItemName(groups);
    return totalPriceDatum;
  }

  static projectToOrderCountDatumByDate(orders: Order[]): ChartDatum | null {
    if (orders.length <= 0) return null;

    const groups = OrderDomainService.createDictionaryByDate(orders);
    const dateList = Object.keys(groups).map((date) => new Date(date));
    const xList = DateDomainService.rangeFromMinToMax(dateList);
    const orderCountDatum: ChartDatum = {
      id: 'orderCount',
      data: xList.map((date) => {
        const x = DateDomainService.format(date);
        const y = groups[x] ? groups[x].length : 0;
        return { x, y };
      }),
    };
    return orderCountDatum;
  }

  static projectToOrderCountDatumByStoreName(
    orders: Order[]
  ): ChartDatum | null {
    if (orders.length <= 0) return null;

    const groups = OrderDomainService.createDictionaryByStoreName(orders);

    const orderCountDatum: ChartDatum = {
      id: 'orderCount',
      data: Object.keys(groups).map((storeName) => {
        const y = groups[storeName].length;
        return { x: storeName, y };
      }),
    };
    return orderCountDatum;
  }

  static projectToItemTotalPriceDatumByItemName(
    items: Item[]
  ): ChartDatum | null {
    if (items.length <= 0) return null;

    const groups = ItemDomainService.createDictionaryByName(items);

    const totalPriceDatum: ChartDatum = {
      id: 'totalPrice',
      data: Object.keys(groups).map((itemName) => {
        const y = sumBy(groups[itemName], (i) => i.total_price || 0);
        return { x: itemName, y };
      }),
    };
    return totalPriceDatum;
  }

  static projectToItemCountDatumByItemName(items: Item[]): ChartDatum | null {
    if (items.length <= 0) return null;

    const groups = ItemDomainService.createDictionaryByName(items);

    const orderCountDatum: ChartDatum = {
      id: 'orderCount',
      data: Object.keys(groups).map((itemName) => {
        const y = groups[itemName].reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        return { x: itemName, y };
      }),
    };
    return orderCountDatum;
  }

  static projectToTotalPriceDatumByDayOfTheWeek(
    orders: Order[]
  ): ChartDatum | null {
    if (orders.length <= 0) return null;

    const groups = OrderDomainService.createDictionaryByDayOfTheWeek(orders);

    const totalPriceDatum: ChartDatum = {
      id: 'totalPrice',
      data: Days.map((date) => {
        const x = date;
        const y = sumBy(groups[x], (o) => OrderDomainService.totalPrice(o));
        return { x, y };
      }),
    };
    return totalPriceDatum;
  }

  static projectToTotalPriceDatumByHourOfTheDay(
    orders: Order[]
  ): ChartDatum | null {
    if (orders.length <= 0) return null;

    const groups = OrderDomainService.createDictionaryByHourOfTheDay(orders);

    const totalPriceDatum: ChartDatum = {
      id: 'totalPrice',
      data: Hours.map((date) => {
        const x = date;
        const y = sumBy(groups[x], (o) => OrderDomainService.totalPrice(o));
        return { x, y };
      }),
    };
    return totalPriceDatum;
  }

  static totalPrice(data: PieDatum[]): number {
    const totalPrice = data.reduce(
      (previousPrice, currentPrice) => (previousPrice += currentPrice.value),
      0
    );
    return totalPrice;
  }

  private static totalPriceByItemName(groups: Dictionary<Order[]>): ChartDatum {
    return {
      id: 'totalPrice',
      data: Object.keys(groups).map((itemName) => {
        const totalPrice = sumBy(
          groups[itemName],
          (i) => i.item_total_price || 0
        );
        return { x: itemName, y: totalPrice };
      }),
    };
  }
}
